<script lang="ts">
import type { VariantProps } from 'tailwind-variants';
import type { ThemeSlots } from '../../types';
import { tv } from 'tailwind-variants';

const listItem = tv({
	slots: {
		base: `
			group/list-item relative flex items-center justify-between
			not-last:after:absolute not-last:after:bottom-0 not-last:after:h-px not-last:after:bg-(--ui-border)
			focus-visible:outline focus-visible:outline-primary
			data-dnd-dragging:bg-default data-dnd-dragging:shadow-lg/3
			data-dnd-dragging:after:bg-transparent
		`,
		content: 'flex min-w-0 flex-1 items-center gap-x-4 self-stretch overflow-x-clip',
		leading: 'flex items-center',
		trailing: '',
		textWrapper: 'flex flex-col overflow-hidden',
		label: 'truncate text-sm font-medium',
		description: 'truncate text-xs font-normal text-dimmed',
		handle: `
			absolute top-1/2 left-0.5 flex -translate-y-1/2 cursor-move items-center bg-(--ui-text-dimmed)/50 px-0.5 opacity-0
			transition-opacity
			group-hover/list-item:opacity-100 group-hover/list-item:duration-0
			group-data-dnd-dragging/list-item:bg-(--ui-text-muted) group-data-dnd-dragging/list-item:opacity-100
			hover:bg-(--ui-text-muted)
		`,
	},
	variants: {
		selectable: {
			true: {
				base: 'cursor-pointer transition-colors hover:bg-muted',
			},
		},
		listDisabled: {
			true: {
				base: 'cursor-not-allowed opacity-50',
			},
		},
		size: {
			sm: {
				base: 'px-3 py-2 not-last:after:inset-x-2',
			},
			md: {
				base: 'px-6 py-3 not-last:after:inset-x-4',
			},
		},
	},
	compoundVariants: [
		{
			selectable: true,
			listDisabled: true,
			class: {
				base: 'cursor-not-allowed hover:bg-transparent',
			},
		},
	],
	defaultVariants: {
		size: 'md',
	},
});

type ListItemVariants = VariantProps<typeof listItem>;

export interface ListItem {
	id: string | number;
	class?: any;
	sortable?: boolean;
}

type SlotProps<T extends ListItem> = (props: { item: T; index: number }) => any;

export interface ListItemProps<T extends ListItem> {
	item: T;
	index: number;
	sortable?: boolean;
	labelKey: keyof T | string;
	descriptionKey: keyof T | string;
	listDisabled?: boolean;
	selectable?: boolean;
	allowDragOnlyOnHandle?: boolean;
	class?: string;
	size?: ListItemVariants['size'];
	ui?: ThemeSlots<typeof listItem>;
}

export interface ListItemSlots<T extends ListItem> {
	default: SlotProps<T>;
	content: SlotProps<T>;
	label: SlotProps<T>;
	description: SlotProps<T>;
	leading: SlotProps<T>;
	trailing: SlotProps<T>;
}
</script>

<script setup lang="ts" generic="T extends ListItem">
import { useSortable } from '@dnd-kit/vue/sortable';
import { computed, useTemplateRef } from 'vue';
import { get } from '#layers/base/utils/object';

const props = defineProps<ListItemProps<T>>();

const slots = defineSlots<ListItemSlots<T>>();

const itemRef = useTemplateRef('item');
const handleRef = useTemplateRef('handle');

useSortable({
	id: () => props.item.id,
	index: () => props.index,
	disabled: () => props.listDisabled || !props.sortable,
	element: () => props.sortable ? itemRef.value : undefined,
	handle: () => props.sortable && props.allowDragOnlyOnHandle ? handleRef.value : undefined,
});

const ui = computed(() => listItem({ listDisabled: props.listDisabled, selectable: props.selectable, size: props.size }));
</script>

<template>
	<li
		ref="item"
		data-test-id="list-item"
		:class="ui.base({ class: [props.class, props.ui?.base, item.class] })"
		:role="selectable && !listDisabled ? 'button' : undefined"
		:tabindex="selectable && !listDisabled ? 0 : undefined"
	>
		<slot v-bind="{ item, index }">
			<DIcon
				v-if="sortable && !listDisabled"
				ref="handle"
				tabindex="-1"
				name="lucide:grip-vertical"
				:class="ui.handle({ class: props.ui?.handle })"
				size="16"
			/>
			<div :class="ui.content({ class: props.ui?.content })">
				<slot name="content" v-bind="{ item, index }">
					<div v-if="!!slots.leading" :class="ui.leading({ class: props.ui?.leading })">
						<slot name="leading" v-bind="{ item, index }" />
					</div>
					<div :class="ui.textWrapper({ class: props.ui?.textWrapper })">
						<span :class="ui.label({ class: props.ui?.label })">
							<slot name="label" v-bind="{ item, index }">
								{{ get(item, labelKey as string) }}
							</slot>
						</span>
						<span v-if="!!slots.description || get(item, descriptionKey as string)" :class="ui.description({ class: props.ui?.description })">
							<slot name="description" v-bind="{ item, index }">
								{{ get(item, descriptionKey as string) }}
							</slot>
						</span>
					</div>
				</slot>
			</div>
			<div
				v-if="!!slots.trailing"
				:class="ui.trailing({ class: props.ui?.trailing })"
			>
				<slot name="trailing" v-bind="{ item, index }" />
			</div>
		</slot>
	</li>
</template>
